import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const Icon = ({ name, className = '' }) => (
  <span className={`material-symbols-outlined ${className}`}>{name}</span>
);

const Nav = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/75 border-b border-black/5">
    <div className="max-w-[1100px] mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
        <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0071e3 0%, #005ec7 100%)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
            <circle cx="9" cy="12" r="3.5" />
            <path d="M15 8 Q 19 6, 21 4" />
            <path d="M14.5 12 L 22 12" />
            <path d="M15 16 Q 19 18, 21 20" />
          </svg>
        </div>
        <span className="text-[17px] font-bold tracking-tight text-[#1d1d1f] lowercase">mindpulsei</span>
      </Link>
      <div className="hidden md:flex items-center gap-7">
        <Link to="/#method" className="text-[12px] text-[#424245] hover:text-[#1d1d1f]">Method</Link>
        <Link to="/emotions" className="text-[12px] text-[#424245] hover:text-[#1d1d1f]">Library</Link>
        <Link to="/#organizations" className="text-[12px] text-[#424245] hover:text-[#1d1d1f]">Organizations</Link>
      </div>
    </div>
  </nav>
);


const CHAPTERS = [
  {
    num: '01',
    icon: 'self_improvement',
    title: 'Body',
    desc: 'Your body keeps the score of every role you play. We start by reading its signals — the tension, the fatigue, the calm — and rebuild from the physical ground up, with practices shaped around your life, not a template.'
  },
  {
    num: '02',
    icon: 'visibility',
    title: 'Truth',
    desc: 'Many women learn early to name everyone else\'s feelings before their own. This chapter turns that skill inward: seeing your emotions honestly, naming them precisely, and trusting what you find.'
  },
  {
    num: '03',
    icon: 'spa',
    title: 'Soul',
    desc: 'Simple mind practices that restore a sense of meaning and connection — steadier energy, quieter self-talk, and a relationship with yourself that feels like friendship.'
  },
  {
    num: '04',
    icon: 'hearing',
    title: 'Senses',
    desc: 'How you read a room, a pause, a tone of voice — perception shapes every reaction. Learn how you take the world in, why, and how to choose your response instead of inheriting it.'
  }
];


const ForWomen = () => {
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    const els = document.querySelectorAll('[data-reveal]');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('in-view'); io.unobserve(e.target); }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#1d1d1f] antialiased" data-testid="for-women-page">
      <Nav />

      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 overflow-hidden" style={{ background: 'linear-gradient(180deg, #dbe6f7 0%, #ffffff 40%)' }}>
        <div className="max-w-[1100px] mx-auto px-3 md:px-8">
          <Link to="/" className="inline-flex items-center gap-1 text-[13px] text-[#6e6e73] hover:text-[#1d1d1f] transition-colors mb-8" data-testid="women-back-btn">
            <span aria-hidden>‹</span> Back to home
          </Link>
          <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div className="space-y-6" data-reveal>
              <p className="text-current-teal text-[15px] font-medium tracking-tight">For Women</p>
              <h1 style={{ fontSize: 'clamp(38px, 5.2vw, 64px)', lineHeight: 1.02, letterSpacing: '-0.03em', fontWeight: 600 }} data-testid="women-page-title">
                Emotional intelligence,<br />made for her.
              </h1>
              <p className="text-[#515154]" style={{ fontSize: 'clamp(16px, 1.4vw, 19px)', lineHeight: 1.5, letterSpacing: '-0.005em', maxWidth: '480px' }}>
                Women lead, mother, mentor, and manage — often all in the same day. mindpulsei is a growth practice designed for that reality: assertive without aggressive, present without depleted, ambitious without apologetic.
              </p>
              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Link to="/experts">
                  <button className="px-6 py-3 rounded-full text-[15px] font-medium text-white transition-opacity hover:opacity-90" style={{ background: 'linear-gradient(135deg, #0071e3 0%, #005ec7 100%)' }} data-testid="women-cta-guidance">
                    Get personal guidance
                  </button>
                </Link>
                <a href="#chapters" className="text-current-teal hover:text-ice-mist text-[15px] font-medium transition-colors inline-flex items-center gap-1" data-testid="women-cta-chapters">
                  See the practice <span aria-hidden>›</span>
                </a>
              </div>
            </div>
            <div className="rounded-[32px] overflow-hidden border border-black/5" data-reveal style={{ transitionDelay: '120ms' }}>
              <img
                src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=1200&q=80&auto=format&fit=crop"
                alt="Confident woman leader"
                className="w-full h-full object-cover aspect-[4/5] md:aspect-[4/4.6]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* The practice, chapter by chapter */}
      <section id="chapters" className="relative py-20 md:py-28" data-testid="women-chapters-section">
        <div className="max-w-[1100px] mx-auto px-3 md:px-8">
          <div className="text-center max-w-[720px] mx-auto mb-12 md:mb-16 space-y-5" data-reveal>
            <p className="text-current-teal text-[15px] font-medium tracking-tight">The mindpulsei method, for her</p>
            <h2 style={{ fontSize: 'clamp(30px, 4vw, 48px)', lineHeight: 1.05, letterSpacing: '-0.025em', fontWeight: 600 }}>
              Four chapters. One balanced you.
            </h2>
            <p className="text-[#515154]" style={{ fontSize: 'clamp(15px, 1.2vw, 17px)', lineHeight: 1.5, letterSpacing: '-0.005em' }}>
              Body, truth, soul, and senses — our wholesome approach, tailored to the realities of a woman's emotional world.
            </p>
          </div>
          <div className="space-y-4 md:space-y-5">
            {CHAPTERS.map((c, i) => (
              <div
                key={c.num}
                data-reveal
                style={{ transitionDelay: `${i * 60}ms` }}
                className="rounded-[28px] p-8 md:p-10 bg-white border border-black/5 flex flex-col md:flex-row md:items-center gap-6 md:gap-10 transition-colors hover:border-black/10"
                data-testid={`women-chapter-${i + 1}`}
              >
                <div className="flex items-center gap-5 md:w-[220px] flex-shrink-0">
                  <div className="h-12 w-12 rounded-2xl flex items-center justify-center bg-current-teal/10 flex-shrink-0">
                    <Icon name={c.icon} className="text-current-teal !text-[24px]" />
                  </div>
                  <div>
                    <p className="text-[#86868b] text-[12px] font-semibold tracking-widest">{c.num}</p>
                    <h3 className="text-[22px] font-semibold" style={{ lineHeight: 1.1, letterSpacing: '-0.02em' }}>{c.title}.</h3>
                  </div>
                </div>
                <p className="text-[#515154] text-[15px]" style={{ lineHeight: 1.55, letterSpacing: '-0.005em' }}>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28" data-testid="women-cta-section">
        <div className="max-w-[1100px] mx-auto px-3 md:px-8">
          <div
            data-reveal
            className="rounded-[28px] p-8 md:p-14 text-center text-white"
            style={{ background: 'linear-gradient(135deg, #0071e3 0%, #005ec7 100%)' }}
          >
            <p className="text-white/80 text-[13px] font-medium tracking-tight mb-3">Begin when you're ready</p>
            <p className="mx-auto max-w-[680px]" style={{ fontSize: 'clamp(22px, 2.6vw, 34px)', lineHeight: 1.2, letterSpacing: '-0.02em', fontWeight: 600 }}>
              Your emotions are not the problem to manage. They're the intelligence to use.
            </p>
            <p className="text-white/85 mx-auto max-w-[560px] mt-4" style={{ fontSize: 'clamp(15px, 1.2vw, 17px)', lineHeight: 1.5 }}>
              Talk to us about where you are and where you want to be — we'll shape the practice around you.
            </p>
            <Link to="/experts" className="inline-block mt-8">
              <button className="px-7 py-3.5 rounded-full text-[15px] font-medium bg-white text-[#0071e3] transition-opacity hover:opacity-90" data-testid="women-cta-bottom">
                Get personal guidance
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mini footer */}
      <footer className="border-t border-black/10 py-8" style={{ background: '#f5f5f7' }}>
        <div className="max-w-[1100px] mx-auto px-3 md:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-[#a1a1a6] text-[12px] tracking-tight">© {new Date().getFullYear()} mindpulsei. All rights reserved.</p>
          <Link to="/" className="text-[#6e6e73] text-[12px] hover:text-[#1d1d1f] transition-colors" data-testid="women-footer-home">Back to home</Link>
        </div>
      </footer>
    </div>
  );
};

export default ForWomen;
